import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Tab, ThemeProvider, createTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import FullHeight from "../../../shared/components/FullHeight";
import ProductVariants from "./SupplierTabs/ProductVariants";
import colors from "../../../shared/themes/colors";
import { CustomerProductSuppliersState } from "../models/models";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { getCompany, getSupplierProductVariants } from "../customerProductSuppliersSlice";
import Loader from "../../../shared/components/Loader";
import CompanyDetails from "../../shared/components/CompanyDetails";
import CloseIconButton from "../../../shared/components/CloseIconButton";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";

interface ProductSupplierDetailsProps {
  productId: string;
  onClose: () => void;
}

const ProductSupplierDetails = ({ productId, onClose }: ProductSupplierDetailsProps) => {
  const { supplier, supplierDetails, company, supplierDetailsLoading } =
    useAppSelector<CustomerProductSuppliersState>((state) => state.customerProductSuppliers);
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("marketplace");

  const companyTabs = {
    productVariants: translate("productVariantsTab"),
    about: translate("aboutTab")
  };

  const [value, setValue] = useState(companyTabs.productVariants);

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (supplier) {
      dispatch(getSupplierProductVariants({ productId: productId, supplierId: supplier.id }));
      dispatch(getCompany(supplier.id));
    }
  }, [supplier]);

  const theme = createTheme({
    components: {
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: "auto",
            height: "36px",
            ".MuiTabs-scroller": {
              height: "36px"
            },
            ".MuiTab-root": {
              minHeight: "auto",
              color: colors.secondary.gray20,

              "&.Mui-selected": {
                fontSize: 18,
                fontWeight: 600,
                color: dt.blue
              }
            }
          }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="flex flex-col justify-between h-full w-full"
        sx={{ width: 650, background: colors.primary.white1, color: colors.secondary.gray20, padding: "16px 32px" }}
      >
        <Box className="w-full h-full">
          {supplierDetailsLoading && <Loader />}

          {!supplierDetailsLoading && !!supplierDetails && (
            <TabContext value={value}>
              <TabList onChange={handleChange} className="mb-6 text-18-bold">
                <Tab label={translate("productVariantsTab")} value={companyTabs.productVariants} />
                <Tab label={translate("aboutTab")} value={companyTabs.about} />
              </TabList>

              <CloseIconButton onClose={onClose} />

              <FullHeight verticalOffset="60px">
                <TabPanel className="h-full" value={companyTabs.productVariants}>
                  <ProductVariants supplierDetails={supplierDetails} />
                </TabPanel>

                <TabPanel className="h-full" value={companyTabs.about}>
                  <CompanyDetails company={company!} isCompanySearch={true} />
                </TabPanel>
              </FullHeight>
            </TabContext>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ProductSupplierDetails;